import { Component } from '@theme/component';

/**
 * Shop the Look — PDP media-gallery slide.
 *
 * Mobile-only sticky for the product-flat strip. On desktop the strip uses
 * native CSS `position: sticky` (smooth, matching the PLP); this class only acts
 * on mobile, where the horizontal scroll-snap carousel traps sticky. There it
 * translates the strip up while the slide's bottom sits below the viewport
 * bottom — clamped so it never rises above the top of the look image — so it
 * stays pinned near the bottom of the page.
 *
 * Mirrors the MLP shop-the-look sticky-thumbs behaviour (mlp-shop-the-look.js
 * #updateStickyThumbs), which is likewise mobile-only.
 *
 * The slide is rendered twice (mobile carousel + desktop grid); the copy hidden
 * at the current breakpoint has no layout box, so it is skipped and reset.
 */
class GalleryShopTheLook extends Component {
  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
  }

  #onScroll = () => {
    if (this.#rafId != null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update() {
    const flats = this.querySelector('[data-stl-thumbs]');
    if (!(flats instanceof HTMLElement)) return;

    // Desktop uses native CSS sticky; only drive the transform on mobile, where
    // the carousel traps sticky. Clear it otherwise (desktop, or this copy
    // hidden — offsetParent null — at the current breakpoint).
    const isMobile = !window.matchMedia('(min-width: 722px)').matches;
    if (!isMobile || this.offsetParent == null) {
      flats.style.transform = '';
      return;
    }

    const maxTravel = Math.max(0, this.offsetHeight - flats.offsetHeight);
    // How far the slide's bottom sits below the viewport bottom; pull the flats
    // up by that much so they stay pinned near the bottom of the page, clamped
    // so they never rise above the top of the slide. Their resting bottom offset
    // (CSS) sets the distance kept from the viewport bottom while stuck.
    const below = Math.max(0, this.getBoundingClientRect().bottom - window.innerHeight);
    const travel = Math.min(below, maxTravel);

    flats.style.transform = travel > 0 ? `translateY(${-travel}px)` : '';
  }
}

if (!customElements.get('gallery-shop-the-look')) {
  customElements.define('gallery-shop-the-look', GalleryShopTheLook);
}
